import { ApiNote } from '../../api';
import { InternalApiAlertAssignment, InternalApiAlertStatusEvent, InternalApiAlertType } from '../../api-internal';
import { useCallback, useEffect, useState } from 'react';
import { alertAssignmentInternalApi, alertStatusApi, noteInternalApi } from '../../apis';

interface AlertManagementProps {
  alertId: number;
  alertType: InternalApiAlertType;
}

export interface AlertManagementData {
  notes: ApiNote[];
  statusEvents: InternalApiAlertStatusEvent[];
  assignments: InternalApiAlertAssignment[];
}

export type LoadData = (loadNotes: boolean, loadStatusEvents: boolean, loadAssignments: boolean) => Promise<void>;

export const useAlertManagement = (props: AlertManagementProps): [AlertManagementData | null, LoadData] => {
  const [state, setState] = useState<AlertManagementData | null>(null);

  const loadData = useCallback(
    async (loadNotes: boolean, loadStatusEvents: boolean, loadAssignments: boolean) => {
      const [notes, statusEvents, assignments] = await Promise.all([
        loadNotes || !state?.notes ? fetchNotes(props) : Promise.resolve(state.notes),
        loadStatusEvents || !state?.statusEvents ? fetchStatusEvents(props) : Promise.resolve(state.statusEvents),
        loadAssignments || !state?.assignments ? fetchAssignments(props) : Promise.resolve(state.assignments),
      ]);
      setState({
        notes: notes,
        statusEvents: statusEvents,
        assignments: assignments,
      });
    },
    [props.alertId, props.alertType, state?.notes, state?.statusEvents, state?.assignments]
  );

  useEffect(() => {
    setState(null);
    loadData(true, true, true);
  }, [props.alertId, props.alertType]);

  return [state, loadData];
};

const fetchNotes = async (props: AlertManagementProps) => {
  return (
    await noteInternalApi.findAllAlertNotes({
      alertId: props.alertId,
      alertType: InternalApiAlertType[props.alertType],
      page: 0,
      size: 20,
    })
  ).content;
};
const fetchStatusEvents = async (props: AlertManagementProps) => {
  return await alertStatusApi.getAllAlertStatusUpdates({
    alertId: props.alertId,
    alertType: props.alertType,
  });
};
const fetchAssignments = async (props: AlertManagementProps) => {
  return await alertAssignmentInternalApi.getAllAssignments({ alertId: props.alertId, alertType: props.alertType });
};
